import React, { useMemo } from 'react';
import { Button, SparkIcon } from '@spark';
import { handleCopy } from '@/shared/utils/helpers';
import ModelDetailsSectionTitle from './ModelDetailsSectionTitle';

import '@/app/styles/scrollbar.css';

export default function ModelDetailsCodeSection({
  title,
  text,
  copyText,
  minHeight = 130,
  maxHeight = 130,
  flexGrow = false,
  titleVariant = 'heading-3-semibold',
  /** Optional node rendered inline next to the title (e.g. a status tag). */
  titleExtra = null,
}) {
  const canCopy = Boolean(copyText && String(copyText).trim().length > 0);

  const displayText = useMemo(() => {
    if (text === null || text === undefined) return '—';
    const asString = typeof text === 'string' ? text : String(text);
    return asString.trim().length > 0 ? asString : '—';
  }, [text]);

  return (
    <div style={flexGrow ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}>
      {titleExtra ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ModelDetailsSectionTitle variant={titleVariant} style={{ marginBottom: 0 }}>
            {title}
          </ModelDetailsSectionTitle>
          {titleExtra}
        </div>
      ) : (
        <ModelDetailsSectionTitle variant={titleVariant}>{title}</ModelDetailsSectionTitle>
      )}

      <div
        style={{
          background: '#F3F4F6',
          borderRadius: 10,
          padding: 16,
          position: 'relative',
          /* No `minHeight: 0` here: the box must never shrink below the
             `pre`'s min height, or the text would overflow past the gray
             background. `min-height: auto` keeps the background wrapping
             the content while `flex: 1` still lets it grow. */
          ...(flexGrow ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}),
        }}
      >
        {/* Icon-only Spark buttons are wrapped in a TooltipTrigger, so the
            absolute positioning must live on an outer wrapper (same pattern
            as CopyFieldButton). */}
        <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
          <Button
            variant="secondary"
            size="small"
            startIcon={<SparkIcon slug="copy" size="small" />}
            onClick={() => handleCopy(String(copyText ?? ''))}
            disabled={!canCopy}
            aria-label="Copy"
            tooltip="Copy"
          />
        </div>

        <pre
          className="optune-scrollbar"
          style={{
            margin: 0,
            ...(flexGrow ? { flex: 1, minHeight: 0 } : { maxHeight }),
            minHeight,
            overflow: 'auto',
            paddingRight: 44,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 12,
            lineHeight: 1.5,
            color: '#111827',
          }}
        >
          {displayText}
        </pre>
      </div>
    </div>
  );
}
